import { AuthContext, IntlContext } from "context";
import { Role } from "types/user";
import React, { useContext, useMemo } from "react";
import { NavLink } from "react-router-dom";
import {
    Clock,
    FileText,
    Heart,
    Send,
    Settings,
    User,
    Users,
    type LucideIcon,
} from "lucide-react";
import { ROUTES } from "routes";
import { cn } from "lib/utils";

interface NavTabItem {
    key: string;
    url: string;
    labelId: string;
    icon: LucideIcon;
    end?: boolean;
}

const NavTabs: React.FC = () => {
    const { currentRole } = useContext(AuthContext);
    const intl = useContext(IntlContext);
    const baseUrl = `${ROUTES.ACCOUNT}/`;

    const navTabs = useMemo((): NavTabItem[] => {
        const tabs: NavTabItem[] = [];

        tabs.push(
            { key: "my_profile", url: `${baseUrl}profil/`, labelId: "profile.nav.my_profile", icon: User },
        );

        if (currentRole === Role.PROVIDER) {
            tabs.push(
                { key: "my_services", url: `${baseUrl}mes-services/`, labelId: "profile.nav.my_services", icon: FileText },
                { key: "my_applications", url: `${baseUrl}mes-candidatures/`, labelId: "profile.nav.my_applications", icon: Send },
                { key: "my_favorites", url: `${baseUrl}mes-favoris/`, labelId: "profile.nav.my_favorites", icon: Heart },
            );
        }

        if (currentRole === Role.COMPANY) {
            tabs.push({
                key: "collaborators",
                url: `${baseUrl}collaborateurs/`,
                labelId: "profile.nav.collaborators",
                icon: Users,
            });
        }

        if (currentRole === Role.CLIENT) {
            tabs.push(
                { key: "my_ads", url: `${ROUTES.MY_ADS}/`, labelId: "profile.nav.my_ads", icon: FileText },
                { key: "my_providers", url: `${baseUrl}mes-prestataires/`, labelId: "profile.nav.my_providers", icon: Heart },
            );
        }

        tabs.push(
            { key: "history", url: `${baseUrl}historique/`, labelId: "profile.nav.service_history", icon: Clock },
            { key: "settings", url: `${baseUrl}parametres/`, labelId: "profile.nav.settings", icon: Settings, end: true }
        );

        return tabs;
    }, [baseUrl, currentRole]);

    return (
        <div className="overflow-x-auto">
            <nav className="flex min-w-max border-b border-gray-200" aria-label="Profile sections">
                {navTabs.map((tab) => {
                    const Icon = tab.icon;
                    const label = intl.formatMessage(tab.labelId);

                    return (
                        <NavLink
                            end={tab.end ?? tab.url === baseUrl}
                            className={({ isActive }) =>
                                cn(
                                    "-mb-px inline-flex shrink-0 items-center gap-2 border-b-2 px-5 py-4 text-sm font-medium transition-colors",
                                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                                    isActive
                                        ? "border-primary text-gray-900"
                                        : "border-transparent text-slate-500 hover:text-slate-700"
                                )
                            }
                            to={tab.url}
                            key={tab.key}
                        >
                            {({ isActive }) => (
                                <>
                                    <Icon
                                        className={cn("h-4 w-4", isActive ? "text-gray-900" : "text-slate-400")}
                                        strokeWidth={isActive ? 2.25 : 2}
                                        aria-hidden
                                    />
                                    {label}
                                </>
                            )}
                        </NavLink>
                    );
                })}
            </nav>
        </div>
    );
};

export default NavTabs;
